import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './HomeOwnerDashboard.module.css';
import axios from 'axios';
import Toast from '../../components/common/Toast/Toast';
import { proposalComparisonService, preferencesService, homeownerBillingService, userService } from '../../services/api';
import guestSessionService from '../../services/guestSessionService';

import PersonalityQuestionnaire from '../../components/common/PersonalityQuestionnaire/PersonalityQuestionnaire';
import PreferencesModal from '../../components/common/PreferencesModal/PreferencesModal';
import PlanUsageModal from '../../components/common/PlanUsageModal/PlanUsageModal';
import ImportConfirmationModal from '../../components/common/ImportConfirmationModal/ImportConfirmationModal';
import AdSenseAd from '../../components/ads/AdSenseAd/AdSenseAd';
import MockAd from '../../components/ads/MockAd/MockAd';

const HomeOwnerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [projectBudget, setProjectBudget] = useState('');

  // Function to validate project name - only allow alphabets, numbers, and spaces
  const validateProjectName = (value) => {
    // Remove any characters that are not alphabets, numbers, or spaces
    return value.replace(/[^a-zA-Z0-9\s]/g, '');
  };
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [contractorDocuments, setContractorDocuments] = useState({
    contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
    contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
  });
  const [addedContractors, setAddedContractors] = useState([]);
  const [editingContractorIndex, setEditingContractorIndex] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isBehavioralNoteRecording, setIsBehavioralNoteRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [behavioralNoteRecognition, setBehavioralNoteRecognition] = useState(null);
  const [editingComparisonId, setEditingComparisonId] = useState(null);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [existingProjectDocuments, setExistingProjectDocuments] = useState([]);
  const [existingContractorDocuments, setExistingContractorDocuments] = useState({});
  const [activeContractorKey, setActiveContractorKey] = useState(null); // Track which contractor's notes are being recorded
  const [contractorKeys, setContractorKeys] = useState(['contractor1', 'contractor2']); // Dynamic contractor keys
  const [collapsedContractors, setCollapsedContractors] = useState({}); // Track collapsed state for each contractor
  
  // Subscription-based user plan detection
  const [userPlan, setUserPlan] = useState('free'); // free, essential, complete, expert
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingComparison, setDeletingComparison] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showPlanUsageModal, setShowPlanUsageModal] = useState(false);
  const [showImportConfirmationModal, setShowImportConfirmationModal] = useState(false);
  const [pendingMigrationData, setPendingMigrationData] = useState(null);
  const [processingAI, setProcessingAI] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  
  // Comparison limit tracking
  const [comparisonLimit, setComparisonLimit] = useState({
    current: 0,
    max: 2,
    planName: 'free',
    addonComparisons: 0
  });
  
  // Track which comparisons are currently being processed
  const [processingComparisons, setProcessingComparisons] = useState(new Set());
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // Layout states
  const [viewMode, setViewMode] = useState('grid-2'); // 'grid-1', 'grid-2', 'grid-3'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'

  // Add missing state variables for file deletion tracking
  const [deletedProjectFiles, setDeletedProjectFiles] = useState([]);
  const [deletedContractorFiles, setDeletedContractorFiles] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Add refs for file inputs to reset them after operations
  const projectFileInputRef = useRef(null);
  const contractorFileInputRefs = useRef({});

  // Plan feature mapping
  const planFeatures = {
    free: {
      maxContractors: 2,
      fileStorage: '250MB',
      hasBehavioralNotes: false,
      hasContractorRatings: false,
      hasAdvancedFeatures: false
    },
    essential: {
      maxContractors: 3,
      fileStorage: '250MB',
      hasBehavioralNotes: true,
      hasContractorRatings: true,
      hasAdvancedFeatures: true
    },
    complete: {
      maxContractors: 5,
      fileStorage: '1GB',
      hasBehavioralNotes: true,
      hasContractorRatings: true,
      hasAdvancedFeatures: true
    },
    expert: {
      maxContractors: 5,
      fileStorage: '5GB',
      hasBehavioralNotes: true,
      hasContractorRatings: true,
      hasAdvancedFeatures: true
    }
  };

  // Get current plan features
  const getCurrentPlanFeatures = () => planFeatures[userPlan] || planFeatures.free;
  const isPremiumUser = userPlan !== 'free';

  // Initialize speech recognition for both fields
  useEffect(() => {
    // Check if browser supports speech recognition
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      // Project description recognition
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setProjectDescription(transcript);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsRecording(false);
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
      
      // Behavioral notes recognition
      const behavioralNotesInstance = new SpeechRecognition();
      behavioralNotesInstance.continuous = true;
      behavioralNotesInstance.interimResults = true;
      behavioralNotesInstance.lang = 'en-US';
      
      behavioralNotesInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        setContractorDocuments(prevDocuments => ({
          ...prevDocuments,
          [activeContractorKey]: {
            ...prevDocuments[activeContractorKey],
            behavioralNotes: transcript
          }
        }));
      };
      
      behavioralNotesInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsBehavioralNoteRecording(false);
      };
      
      behavioralNotesInstance.onend = () => {
        setIsBehavioralNoteRecording(false);
      };
      
      setBehavioralNoteRecognition(behavioralNotesInstance);
    }
  }, [activeContractorKey]);

  // Handle voice recording for project description
  const toggleRecording = () => {
    if (!recognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
    } else {
      // Stop the other recording if it's active
      if (isBehavioralNoteRecording && behavioralNoteRecognition) {
        behavioralNoteRecognition.stop();
        setIsBehavioralNoteRecording(false);
      }
      recognition.start();
      setIsRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Handle voice recording for behavioral notes
  const toggleBehavioralNoteRecording = () => {
    if (!behavioralNoteRecognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (!activeContractorKey) {
      setToast({
        type: 'error',
        message: 'Please select a contractor first'
      });
      return;
    }
    
    if (isBehavioralNoteRecording) {
      behavioralNoteRecognition.stop();
      setIsBehavioralNoteRecording(false);
    } else {
      // Stop the other recording if it's active
      if (isRecording && recognition) {
        recognition.stop();
        setIsRecording(false);
      }
      setActiveContractorKey(activeContractorKey); // Ensure we have the right contractor
      behavioralNoteRecognition.start();
      setIsBehavioralNoteRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Fetch user subscription status and comparison limits
  const fetchUserSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      
      // Fetch subscription and user stats in parallel
      const [subscriptionResponse, userStatsResponse, addonResponse] = await Promise.all([
        homeownerBillingService.getSubscription(),
        userService.getUserStats(),
        homeownerBillingService.getUserAddonComparisons().catch(() => ({ data: { data: { addonComparisons: 0 } } }))
      ]);
      
      let planName = 'free';
      let currentComparisons = 0;
      let addonComparisons = 0;
      
      // Process subscription data
      if (subscriptionResponse.data.status === 'success') {
        const fetchedSubscriptionData = subscriptionResponse.data.data;
        setSubscriptionData(fetchedSubscriptionData);
        
        if (fetchedSubscriptionData && fetchedSubscriptionData.status === 'active') {
          planName = fetchedSubscriptionData.plan_name.toLowerCase();
          console.log('User subscription found:', planName);
        } else {
          console.log('No active subscription, user is on free plan');
        }
      } else {
        console.log('API error, defaulting to free plan');
        setSubscriptionData(null);
      }
      
      // Process user stats
      if (userStatsResponse.data.status === 'success') {
        currentComparisons = userStatsResponse.data.data.proposalComparisons || 0;
      }
      
      // Process addon comparisons
      if (addonResponse.data.status === 'success') {
        addonComparisons = Number(addonResponse.data.data.addonComparisons) || 0;
      }
      
      // Calculate comparison limits based on plan
      let baseLimit;
      switch (planName) {
        case 'essential':
          baseLimit = 5;
          break;
        case 'complete':
          baseLimit = 10;
          break;
        case 'expert':
          baseLimit = 20;
          break;
        case 'free':
        default:
          baseLimit = 2;
          break;
      }
      
      const totalLimit = baseLimit + addonComparisons;
      
      setUserPlan(planName);
      setComparisonLimit({
        current: currentComparisons,
        max: totalLimit,
        planName: planName,
        addonComparisons: addonComparisons
      });
      
      console.log('Comparison limits updated:', {
        current: currentComparisons,
        max: totalLimit,
        planName: planName,
        addonComparisons: addonComparisons
      });
      
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      // On error, default to free plan
      setUserPlan('free');
      setComparisonLimit({
        current: 0,
        max: 2,
        planName: 'free',
        addonComparisons: 0
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch the user's existing comparisons
  useEffect(() => {
    const fetchComparisons = async () => {
      try {
        setLoading(true);
        // Use our API service to fetch comparisons
        const response = await proposalComparisonService.getUserComparisons();
        console.log('Received comparisons data:', response);
        
        if (response.data && response.data.status === 'success') {
          // Map the response data to the format expected by the component
          const mappedComparisons = response.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            hasPremiumAnalysis: comparison.has_premium_analysis || false,
            data: comparison
          }));
          
          console.log('Mapped comparisons:', mappedComparisons);
          setComparisons(mappedComparisons);
          
          // Check background process status for all comparisons
          await checkAllComparisonStatuses();
        } else {
          console.error('Unexpected response format:', response);
          setComparisons([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching comparisons:', err);
        setError('Failed to load comparisons');
        setToast({
          type: 'error',
          message: 'Failed to load your comparisons'
        });
        setLoading(false);
      }
    };

    // Check for guest projects to import and show confirmation modal
    const checkForGuestProjectsImport = async () => {
      console.log('=== CHECKING FOR GUEST PROJECTS IMPORT ===');
      const pendingMigration = localStorage.getItem('pendingGuestMigration');
      console.log('Pending migration data:', pendingMigration);
      
      if (pendingMigration) {
        try {
          const migrationInfo = JSON.parse(pendingMigration);
          console.log('Parsed migration info:', migrationInfo);
          
          if (migrationInfo.guestSessions?.length > 0) {
            console.log('Found', migrationInfo.guestSessions.length, 'guest projects to import');
            
            // Extract comparison IDs from guest sessions
            const comparisonIds = migrationInfo.guestSessions
              .map(session => session.comparisonId)
              .filter(id => id);
            
            if (comparisonIds.length === 0) {
              console.warn('No valid comparison IDs found in guest sessions');
              localStorage.removeItem('pendingGuestMigration');
              return;
            }
            
            // Store migration data and show confirmation modal
            setPendingMigrationData({
              userId: migrationInfo.userId,
              guestSessions: migrationInfo.guestSessions
            });
            setShowImportConfirmationModal(true);
          } else {
            console.log('No guest sessions to import');
            localStorage.removeItem('pendingGuestMigration');
          }
        } catch (error) {
          console.error('Error parsing pending migration data:', error);
          localStorage.removeItem('pendingGuestMigration');
        }
      } else {
        console.log('No pending migration found');
      }
      console.log('=== END CHECKING FOR GUEST PROJECTS IMPORT ===');
    };

    fetchComparisons();
    checkForGuestProjectsImport();
    fetchUserSubscription(); // Fetch subscription status
  }, []);

  // Periodic check for background processes
  useEffect(() => {
    if (comparisons.length === 0) return;

    const interval = setInterval(async () => {
      await checkAllComparisonStatuses();
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [comparisons]);

  // Listen for subscription changes (e.g., when user upgrades)
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      fetchUserSubscription();
    };

    const handlePlanUsageRefresh = () => {
      fetchUserSubscription();
    };

    // Listen for custom events
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    window.addEventListener('refreshPlanUsage', handlePlanUsageRefresh);
    
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
      window.removeEventListener('refreshPlanUsage', handlePlanUsageRefresh);
    };
  }, []);

  // Check for first-time user after subscription data is loaded
  useEffect(() => {
    if (!subscriptionLoading && userPlan !== 'free') {
      checkForFirstTimeUser();
    }
  }, [subscriptionLoading, userPlan]);

  // Check if this is a first-time user who hasn't completed the questionnaire
  const checkForFirstTimeUser = async () => {
    try {
      // Check if this is a fresh login (not a page refresh)
      const isFreshLogin = sessionStorage.getItem('freshLogin') === 'true';
      
      if (!isFreshLogin) {
        // Not a fresh login, don't show questionnaire
        return;
      }
      
      // Clear the fresh login flag
      sessionStorage.removeItem('freshLogin');
      
      const response = await preferencesService.checkStatus();
      if (response.data.status === 'success' && !response.data.data.hasCompleted) {
        // Show questionnaire after a short delay to let the dashboard load first
        setTimeout(() => {
          setShowQuestionnaire(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error checking preferences status:', error);
      // If there's an error checking status, don't show questionnaire
      // This ensures we only rely on backend data
    }
  };

  // Check for openModal parameter and automatically open create modal
  useEffect(() => {
    const openModal = searchParams.get('openModal');
    if (openModal === 'true') {
      // Remove the parameter from URL to clean it up
      searchParams.delete('openModal');
      setSearchParams(searchParams, { replace: true });
      
      // Open the create modal after a short delay to let the component load
      setTimeout(() => {
        handleCreateComparison();
      }, 500);
    }
  }, [searchParams, setSearchParams]);

  const handleCreateComparison = () => {
    // Check if user has reached their comparison limit
    if (comparisonLimit.current >= comparisonLimit.max) {
      setToast({
        type: 'warning',
        message: `You've reached your comparison limit of ${comparisonLimit.max} for your ${comparisonLimit.planName.charAt(0).toUpperCase() + comparisonLimit.planName.slice(1)} plan. Upgrade your plan or purchase additional comparisons to continue.`
      });
      
      // Navigate to pricing page after a short delay
      setTimeout(() => {
        navigate('/homeowner/pricing');
      }, 3000);
      
      return;
    }
    
    setShowCreateModal(true);
    setCurrentStep(1);
    resetForm();
  };



  const handleCloseModal = () => {
    // Stop recording if active when closing modal
    if (isRecording && recognition) {
      recognition.stop();
      setIsRecording(false);
    }
    if (isBehavioralNoteRecording && behavioralNoteRecognition) {
      behavioralNoteRecognition.stop();
      setIsBehavioralNoteRecording(false);
    }
    setShowCreateModal(false);
    setEditingComparisonId(null); // Reset the editing ID
    setIsEditingProject(false); // Reset editing mode
    setProcessingAI(false); // Reset AI processing state
    setProcessingProgress(0); // Reset progress
    resetForm();
  };

  const handleCancelEdit = () => {
    // Cancel editing and reset state
    setEditingComparisonId(null);
    setIsEditingProject(false);
    setDeletedProjectFiles([]);
    setDeletedContractorFiles([]);
    setSelectedDocuments([]);
    setContractorDocuments({
      contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
      contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
    });
    
    // Reset file input refs
    if (projectFileInputRef.current) {
      projectFileInputRef.current.value = '';
    }
    Object.keys(contractorFileInputRefs.current).forEach(key => {
      if (contractorFileInputRefs.current[key]) {
        contractorFileInputRefs.current[key].value = '';
      }
    });
    
    setShowCreateModal(false);
    resetForm();
  };

  const resetForm = () => {
    setProjectName('');
    setProjectDescription('');
    setProjectLocation('');
    setProjectBudget('');
    setSelectedDocuments([]);
    setCurrentStep(1);
    setContractorKeys(['contractor1', 'contractor2']); // Reset to default 2 contractors
    setContractorDocuments({
      contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
      contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
    });
    setCollapsedContractors({}); // Reset collapsed states
    setEditingContractorIndex(null);
    
    // Reset file input refs
    if (projectFileInputRef.current) {
      projectFileInputRef.current.value = '';
    }
    Object.keys(contractorFileInputRefs.current).forEach(key => {
      if (contractorFileInputRefs.current[key]) {
        contractorFileInputRefs.current[key].value = '';
      }
    });
  };

  // Pagination helper functions
  const getPaginatedItems = (items, page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items, perPage) => {
    return Math.ceil(items.length / perPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Sort comparisons
  const sortedComparisons = [...comparisons].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'newest':
      default:
        return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  // Get paginated data
  const paginatedComparisons = getPaginatedItems(sortedComparisons, currentPage, itemsPerPage);
  const totalPages = getTotalPages(sortedComparisons, itemsPerPage);

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments([...selectedDocuments, ...files]);
    
    // Reset the file input after adding files
    if (e.target) {
      e.target.value = '';
    }
  };

  const removeDocument = (index) => {
    const updatedDocs = [...selectedDocuments];
    updatedDocs.splice(index, 1);
    setSelectedDocuments(updatedDocs);
  };

  const handleContractorFileUpload = (contractorKey, e) => {
    const files = Array.from(e.target.files);
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: {
        ...prev[contractorKey],
        documents: [...prev[contractorKey].documents, ...files]
      }
    }));
    
    // Reset the file input after adding files
    if (e.target) {
      e.target.value = '';
    }
  };

  const removeContractorFile = (contractorKey, fileIndex) => {
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: {
        ...prev[contractorKey],
        documents: prev[contractorKey].documents.filter((_, index) => index !== fileIndex)
      }
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!projectName) {
        setToast({
          type: 'error',
          message: 'Please enter a project name'
        });
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  const handleSubmit = async () => {
    try {
      // Check if user has provided either project description OR uploaded documents
      const hasDescription = projectDescription && projectDescription.trim().length > 0;
      const hasDocuments = selectedDocuments.length > 0;
      
      if (!hasDescription && !hasDocuments) {
        setToast({
          type: 'error',
          message: 'Please either provide a project description or upload project documents. Our AI needs some understanding of your project to provide accurate analysis.'
        });
        return;
      }

      // Prepare contractors data from uploaded files
      const contractors = [];
      Object.entries(contractorDocuments).forEach(([key, contractorData], index) => {
        if (contractorData.documents.length > 0) {
          contractors.push({
            name: `Contractor ${index + 1}`,
            email: '',
            phone: '',
            company: '',
            behavioralNotes: contractorData.behavioralNotes,
            appearance: contractorData.appearance,
            organization: contractorData.organization,
            competency: contractorData.competency,
            documents: contractorData.documents
          });
        }
      });

              const minContractors = 2; // Always require minimum 2 contractors
        if (contractors.length < minContractors) {
          setToast({
            type: 'error',
            message: 'Please upload documents for at least two contractors'
          });
          return;
        }
      
      setLoading(true);
      
      // Prepare the project data from user input
      const projectData = {
        projectName,
        projectDescription,
        projectLocation,
        projectBudget,
        contractors: contractors.map(contractor => ({
          name: contractor.name,
          email: contractor.email,
          phone: contractor.phone,
          company: contractor.company,
          behavioralNotes: contractor.behavioralNotes,
          appearance: contractor.appearance,
          organization: contractor.organization,
          competency: contractor.competency
        }))
      };
      
      // Create the comparison entry in the database
      const response = await proposalComparisonService.createComparison(projectData);
      
      if (response.data && response.data.status === 'success') {
        const comparisonId = response.data.data.id;
        
        // Upload project documents first (SOW files)
        const uploadPromises = [];
        
        // Handle project documents or create description file
        if (selectedDocuments.length > 0 || hasDescription) {
          const projectFormData = new FormData();
          projectFormData.append('projectName', projectName);
          
          // Add all uploaded project document files
          for (let i = 0; i < selectedDocuments.length; i++) {
            console.log(`Adding project file ${i + 1}:`, selectedDocuments[i].name);
            projectFormData.append('files', selectedDocuments[i]);
          }
          
          // If user provided description but no files, create a text file from description
          if (hasDescription && selectedDocuments.length === 0) {
            const descriptionContent = projectDescription;
            
            const descriptionBlob = new Blob([descriptionContent], { type: 'text/plain' });
            const descriptionFile = new File([descriptionBlob], `${projectName.replace(/[^a-zA-Z0-9]/g, '_')}_project_description.txt`, { type: 'text/plain' });
            
            console.log('Creating description file:', descriptionFile.name);
            projectFormData.append('files', descriptionFile);
          }
          
          console.log('Project documents FormData prepared, making upload request...');
          
          // Upload project files using "project" as contractor parameter (routes to sow folder)
          uploadPromises.push(
            proposalComparisonService.uploadContractorFiles(
              comparisonId, 
              "project", 
              projectFormData
            ).then(response => {
              console.log(`Project documents upload successful:`, response);
              return response;
            }).catch(error => {
              console.error(`Error uploading project documents:`, error);
              return null;
            })
          );
        }
        
        // Upload documents for each contractor
        for (const contractor of contractors) {
          if (contractor.documents && contractor.documents.length > 0) {
            console.log('=== FRONTEND: UPLOADING FILES ===');
            console.log('Contractor:', contractor.name);
            console.log('Documents:', contractor.documents.length);
            console.log('Project Name:', projectName);
            console.log('Company:', contractor.company);
            
            const formData = new FormData();
            formData.append('projectName', projectName);
            formData.append('contractor', contractor.name);
            formData.append('company', contractor.company || '');
            
            // Add all document files
            for (let i = 0; i < contractor.documents.length; i++) {
              console.log(`Adding file ${i + 1}:`, contractor.documents[i].name);
              formData.append('files', contractor.documents[i]);
            }
            
            console.log('FormData prepared, making upload request...');
            
            // Use standardized contractor folder naming (contractor_1, contractor_2, etc.)
            const contractorIndex = contractors.findIndex(c => c.name === contractor.name);
            const contractorFolderName = `contractor_${contractorIndex + 1}`;
            
            // Upload files
            uploadPromises.push(
              proposalComparisonService.uploadContractorFiles(
                comparisonId, 
                contractorFolderName, 
                formData
              ).then(response => {
                console.log(`Upload successful for ${contractor.name}:`, response);
                return response;
              }).catch(error => {
                console.error(`Error uploading files for ${contractor.name}:`, error);
                return null;
              })
            );
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setLoading(false);
        setProcessingAI(true);
        setProcessingProgress(5);
        
        console.log('=== FILES UPLOADED, STARTING AI PROCESSING ===');
        
        // Realistic progress simulation - slow increase over 30 seconds, then stop at 99%
        const progressInterval = setInterval(() => {
          setProcessingProgress(prev => {
            // Slow progress for first 30 seconds (reaches ~95%)
            if (prev < 95) {
              return Math.min(prev + Math.random() * 3, 95);
            }
            // After 30 seconds, gradually approach 99% but never reach 100%
            if (prev < 99) {
              return Math.min(prev + 0.5, 99);
            }
            // Stay at 99% until actual completion
            return 99;
          });
        }, 1000); // Update every 1 second for smoother animation
        
        // Start AI processing after files are uploaded
        try {
          setToast({
            type: 'info',
            message: 'Files uploaded successfully! Starting AI analysis...'
          });
          
          console.log('Starting AI processing API call...');
          
          const aiResponse = await proposalComparisonService.startAIProcessing(comparisonId);
          
          console.log('AI processing API call completed:', aiResponse);
          
          // Stop the progress simulation and set to 100%
          clearInterval(progressInterval);
          setProcessingProgress(100);
          
          if (aiResponse.data && aiResponse.data.status === 'success') {
            console.log('✅ AI processing API call completed:', aiResponse.data);
            
            // Check if this is a premium user with background processing
            if (aiResponse.data.data && aiResponse.data.data.isPremiumAnalysis === true && aiResponse.data.data.processingInBackground === true) {
              console.log('🌟 Premium user detected - background processing started');
              
              // Stop the progress immediately and reset
              clearInterval(progressInterval);
              setProcessingAI(false);
              setProcessingProgress(0);
              
              setToast({
                type: 'info',
                message: `🌟 Premium Analysis Started! ${aiResponse.data.message || 'You will receive an email notification when your detailed analysis is complete (typically 3-5 minutes).'}`
              });
              
              // Close modal immediately since analysis will continue in background
              setShowCreateModal(false);
              resetForm();
              
            } else {
              // Regular free user flow - analysis completed
              console.log('✅ Standard analysis completed successfully');
              
              setToast({
                type: 'success',
                message: 'AI analysis completed successfully! Project created.'
              });
              
              // Small delay to show 100% completion and success message
              setTimeout(() => {
                setProcessingAI(false);
                setProcessingProgress(0);
                
                // Close modal and reset form
                setShowCreateModal(false);
                resetForm();
              }, 2000);
            }
          } else {
            throw new Error('Failed to complete AI processing');
          }
        } catch (aiError) {
          clearInterval(progressInterval);
          console.error('Error during AI processing:', aiError);
          
          setProcessingAI(false);
          setProcessingProgress(0);
          
          setToast({
            type: 'warning',
            message: 'Project created successfully, but AI analysis failed. You can view the project in your dashboard.'
          });
          
          // Close modal and reset form even if AI fails
          setShowCreateModal(false);
          resetForm();
        }
        
        // Refresh the comparisons list and update limits
        const [updatedComparisons, updatedUserStats] = await Promise.all([
          proposalComparisonService.getUserComparisons(),
          userService.getUserStats()
        ]);
        
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            hasPremiumAnalysis: comparison.has_premium_analysis || false,
            data: comparison
          })));
          
          // Update comparison limits
          if (updatedUserStats.data && updatedUserStats.data.status === 'success') {
            const newCurrentCount = updatedUserStats.data.data.proposalComparisons || 0;
            setComparisonLimit(prev => ({
              ...prev,
              current: newCurrentCount
            }));
          }
          
          // Trigger refresh of plan usage data in navbar
          window.dispatchEvent(new CustomEvent('refreshPlanUsage'));
        }
      } else {
        throw new Error('Failed to create project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error creating project:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to create project'
      });
      setLoading(false);
    }
  };

  const handleAddContractorToExisting = async (comparisonId) => {
    try {
      // Get the existing comparison data
      const existingComparison = comparisons.find(c => c.id === comparisonId);
      
      if (!existingComparison) {
        throw new Error('Project not found');
      }
      
      // Get existing contractors from the project
      const existingContractors = existingComparison.data.contractors || [];
      const existingContractorCount = existingContractors.length;
      
      // Identify new contractors - those with documents to upload
      const newContractors = [];
      Object.entries(contractorDocuments).forEach(([key, contractorData]) => {
        if (contractorData.documents && contractorData.documents.length > 0) {
          // This is a new contractor with documents
          const newContractorIndex = newContractors.length + 1; // 1-based indexing for naming
          newContractors.push({
            name: `Contractor ${existingContractorCount + newContractorIndex}`,
            email: '',
            phone: '',
            company: '',
            behavioralNotes: contractorData.behavioralNotes || '',
            appearance: contractorData.appearance || 0,
            organization: contractorData.organization || 0,
            competency: contractorData.competency || 0,
            documents: contractorData.documents,
            folderName: `contractor_${existingContractorCount + newContractorIndex}` // Proper folder naming
          });
        }
      });
      
      if (newContractors.length === 0) {
        setToast({
          type: 'error',
          message: 'Please add at least one contractor with documents'
        });
        return;
      }
      
      setLoading(true);
      
      // Combine existing contractors with new contractors for database update
      const allContractors = [
        ...existingContractors,
        ...newContractors.map(contractor => ({
          name: contractor.name,
          email: contractor.email,
          phone: contractor.phone,
          company: contractor.company,
          behavioralNotes: contractor.behavioralNotes,
          appearance: contractor.appearance,
          organization: contractor.organization,
          competency: contractor.competency
        }))
      ];
      
      const updateData = {
        projectName,
        projectDescription,
        projectLocation,
        projectBudget,
        contractors: allContractors,
        changeType: 'contractor_only' // Adding new contractors → Step 2 processing
      };
      
      // Update the comparison in the database
      const response = await proposalComparisonService.updateComparison(comparisonId, updateData);
      
      if (response.data && response.data.status === 'success') {
        // Upload documents for the new contractors only
        const uploadPromises = [];
        
        for (const contractor of newContractors) {
          console.log('=== FRONTEND: UPLOADING FILES (ADD CONTRACTOR) ===');
          console.log('Contractor:', contractor.name);
          console.log('Documents:', contractor.documents.length);
          console.log('Project Name:', projectName);
          console.log('Company:', contractor.company);
          console.log('Folder Name:', contractor.folderName);
          
          const formData = new FormData();
          formData.append('projectName', projectName);
          formData.append('contractor', contractor.name);
          formData.append('company', contractor.company || '');
          
          // Add all document files
          for (let i = 0; i < contractor.documents.length; i++) {
            console.log(`Adding file ${i + 1}:`, contractor.documents[i].name);
            formData.append('files', contractor.documents[i]);
          }
          
          console.log('FormData prepared, making upload request...');
          
          // Upload files using the correct folder name
          uploadPromises.push(
            proposalComparisonService.uploadContractorFiles(
              comparisonId, 
              contractor.folderName, 
              formData
            ).then(response => {
              console.log(`Upload successful for ${contractor.name}:`, response);
              return response;
            }).catch(error => {
              console.error(`Error uploading files for ${contractor.name}:`, error);
              // Continue even if one upload fails
              return null;
            })
          );
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setLoading(false);
        
        console.log('=== CONTRACTOR FILES UPLOADED, STARTING AI PROCESSING ===');
        
        // Start AI processing after files are uploaded (paid users only - always background processing)
        try {
          setToast({
            type: 'info',
            message: 'Contractor files uploaded successfully! Starting AI re-analysis...'
          });
          
          console.log('Starting AI processing API call for new contractors...');
          
          const aiResponse = await proposalComparisonService.startAIProcessing(comparisonId);
          
          console.log('AI processing API call completed:', aiResponse);
          
          if (aiResponse.data && aiResponse.data.status === 'success') {
            console.log('✅ AI processing API call completed:', aiResponse.data);
            
            // Since only paid users can add contractors, this will always be premium background processing
            setToast({
              type: 'success',
              message: `🌟 AI Re-Analysis Started! ${aiResponse.data.message || 'You will receive an email notification when your updated comparison is complete (typically 3-5 minutes).'}`
            });
            
            // Close modal since analysis will continue in background
            setTimeout(() => {
              setShowCreateModal(false);
              resetForm();
            }, 3000);
            
          } else {
            throw new Error(aiResponse.data?.message || 'AI processing failed');
          }
          
        } catch (aiError) {
          console.error('Error during AI processing:', aiError);
          
          setToast({
            type: 'warning',
            message: 'Contractors added successfully, but AI re-analysis failed. You can manually trigger analysis later or contact support.'
          });
          
          // Still close modal and refresh since contractors were added successfully
          setTimeout(() => {
            setShowCreateModal(false);
            resetForm();
          }, 3000);
        }
        
        // Refresh the comparisons list
        const updatedComparisons = await proposalComparisonService.getUserComparisons();
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            hasPremiumAnalysis: comparison.has_premium_analysis || false,
            data: comparison
          })));
          
          // Trigger refresh of plan usage data in navbar
          window.dispatchEvent(new CustomEvent('refreshPlanUsage'));
        }
      } else {
        throw new Error('Failed to update project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error adding contractors:', err);
      
      // Handle specific comparison limit error
      if (err.response && err.response.data && err.response.data.code === 'COMPARISON_LIMIT_EXCEEDED') {
        setToast({
          type: 'error',
          message: err.response.data.message
        });
        
        // Navigate to pricing page after a short delay
        setTimeout(() => {
          navigate('/homeowner/pricing');
        }, 3000);
      } else {
        setToast({
          type: 'error',
          message: err.response?.data?.message || err.message || 'Failed to add contractors'
        });
      }
      setLoading(false);
    }
  };

  // Fetch documents for a project when editing
  const fetchProjectDocuments = async (projectId) => {
    try {
      const response = await proposalComparisonService.getComparisonFiles(projectId);
      if (response.data && response.data.status === 'success') {
        // Filter to only show project files (contractor = "project" or "sow")
        const projectFiles = (response.data.data.files || []).filter(file => 
          file.contractor === 'project' || file.contractor === 'sow'
        );
        setExistingProjectDocuments(projectFiles);
      }
    } catch (error) {
      console.error('Error fetching project documents:', error);
      setToast({
        type: 'error',
        message: 'Failed to load project documents'
      });
    }
  };

  // Fetch documents for a contractor when editing
  const fetchContractorDocuments = async (projectId, contractorFolder) => {
    try {
      const response = await proposalComparisonService.getContractorFiles(projectId, contractorFolder);
      if (response.data && response.data.status === 'success') {
        // Map the contractor folder to the contractor key for display
        const contractorKey = contractorKeys.find(key => {
          const keyIndex = parseInt(key.replace('contractor', ''));
          return `contractor_${keyIndex}` === contractorFolder;
        });
        
        if (contractorKey) {
          setExistingContractorDocuments(prev => ({
            ...prev,
            [contractorKey]: response.data.data.files || []
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching documents for ${contractorFolder}:`, error);
    }
  };

  // Delete an existing document
  const handleDeleteExistingDocument = async (fileId, fileName, contractorType) => {
    // All file deletions during editing cost comparisons since they require reanalysis
    const isSOWFile = contractorType === 'project' || contractorType === 'sow';
    const isContractorFile = contractorType && contractorType !== 'project' && contractorType !== 'sow';
    
    // Show confirmation dialog for all file deletions
    let confirmationMessage = '';
    if (isSOWFile) {
      confirmationMessage = 
        `⚠️ COMPARISON COST WARNING ⚠️\n\n` +
        `Deleting "${fileName}" will cost 1 comparison since you'll need to reupload project documents which triggers full AI re-analysis.\n\n` +
        `Current usage: ${comparisonLimit.current}/${comparisonLimit.max} comparisons\n\n` +
        `Are you sure you want to proceed?`;
    } else if (isContractorFile) {
      confirmationMessage = 
        `⚠️ COMPARISON COST WARNING ⚠️\n\n` +
        `Deleting "${fileName}" will trigger AI reanalysis and cost 1 comparison from your plan.\n\n` +
        `Current usage: ${comparisonLimit.current}/${comparisonLimit.max} comparisons\n\n` +
        `Are you sure you want to proceed?`;
    }
    
    const confirmed = window.confirm(confirmationMessage);
    
    if (!confirmed) {
      return; // User cancelled
    }
    
    // Check if user has reached their limit
    if (comparisonLimit.current >= comparisonLimit.max) {
      const fileTypeMessage = isSOWFile ? 'project file' : 'contractor file';
      setToast({
        type: 'error',
        message: `Cannot delete ${fileTypeMessage}: You've reached your comparison limit of ${comparisonLimit.max}. Each file deletion during editing costs 1 comparison since it requires AI reanalysis. Upgrade your plan to continue.`
      });
      return;
    }
    
    try {
      setLoading(true);
      const response = await proposalComparisonService.deleteFile(fileId);
      
      if (response.data && response.data.status === 'success') {
        // Update the existing project documents list
        setExistingProjectDocuments(prev => prev.filter(doc => doc.id !== fileId));
        
        // Update contractor documents if needed
        const updatedContractorDocs = {...existingContractorDocuments};
        Object.keys(updatedContractorDocs).forEach(contractorKey => {
          updatedContractorDocs[contractorKey] = updatedContractorDocs[contractorKey].filter(doc => doc.id !== fileId);
        });
        setExistingContractorDocuments(updatedContractorDocs);
        
        // Update comparison limit since a comparison was used
        setComparisonLimit(prev => ({
          ...prev,
          current: prev.current + 1
        }));
        
        // Trigger refresh of plan usage data in navbar
        window.dispatchEvent(new CustomEvent('refreshPlanUsage'));
        
        // Show appropriate message based on reprocessing info from backend
        const reprocessingInfo = response.data.reprocessing;
        let successMessage = 'Document deleted successfully';
        
        if (reprocessingInfo && reprocessingInfo.includes('1 comparison cost')) {
          if (isSOWFile) {
            successMessage = `Document deleted successfully! Click "Save Changes" to trigger full AI re-analysis (will cost 1 comparison: ${comparisonLimit.current + 1}/${comparisonLimit.max}).`;
          } else {
            successMessage = `Document deleted successfully! Click "Save Changes" to trigger AI re-analysis (will cost 1 comparison: ${comparisonLimit.current + 1}/${comparisonLimit.max}).`;
          }
        }
        
        setToast({
          type: 'success',
          message: successMessage
        });
      } else {
        throw new Error('Failed to delete document');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error deleting document:', error);
      
      // Handle specific comparison limit error
      if (error.response && error.response.data && error.response.data.code === 'COMPARISON_LIMIT_EXCEEDED') {
        setToast({
          type: 'error',
          message: error.response.data.message
        });
        
        // Navigate to pricing page after a short delay
        setTimeout(() => {
          navigate('/homeowner/pricing');
        }, 3000);
      } else {
        setToast({
          type: 'error',
          message: error.response?.data?.message || 'Failed to delete document'
        });
      }
      setLoading(false);
    }
  };

  // Handle editing an existing project
  const handleEditProject = (comparison) => {
    // Set project data to edit
    setProjectName(comparison.name);
    setProjectDescription(comparison.description || '');
    setProjectLocation(comparison.data.project_location || '');
    setProjectBudget(comparison.data.project_budget || '');
    
    // Set up contractor keys based on existing contractors
    const existingContractors = comparison.data.contractors || [];
    const contractorKeysArray = existingContractors.map((_, index) => `contractor${index + 1}`);
    setContractorKeys(contractorKeysArray);
    
    // Set up contractorDocuments state with existing contractor data
    const contractorDocsState = {};
    existingContractors.forEach((contractor, index) => {
      const contractorKey = `contractor${index + 1}`;
      contractorDocsState[contractorKey] = {
        documents: [], // New documents to be uploaded
        behavioralNotes: contractor.behavioralNotes || '',
        appearance: contractor.appearance || 0,
        organization: contractor.organization || 0,
        competency: contractor.competency || 0,
        // Store existing contractor info for reference
        existingName: contractor.name || '',
        existingEmail: contractor.email || '',
        existingPhone: contractor.phone || '',
        existingCompany: contractor.company || ''
      };
    });
    setContractorDocuments(contractorDocsState);
    
    // Set contractors from the existing project (keep this for compatibility)
    setAddedContractors(existingContractors.map(contractor => ({
      name: contractor.name || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      company: contractor.company || '',
      behavioralNotes: contractor.behavioralNotes || '',
      appearance: contractor.appearance || 0,
      organization: contractor.organization || 0,
      competency: contractor.competency || 0,
      documents: [] // New documents to be uploaded
    })));
    
    // Store the comparison ID to use when submitting
    setEditingComparisonId(comparison.id);
    
    // Set editing mode
    setIsEditingProject(true);
    
    // Start at step 1 for project details
    setCurrentStep(1);
    setShowCreateModal(true);
    
    // Fetch existing documents
    fetchProjectDocuments(comparison.id);
    
    // Fetch documents for each contractor using the correct folder structure
    existingContractors.forEach((contractor, index) => {
      const contractorFolderName = `contractor_${index + 1}`;
      fetchContractorDocuments(comparison.id, contractorFolderName);
    });
  };

  // Handle save project edits
  const handleSaveProjectEdits = async () => {
    try {
      // Check if user has provided either project description OR uploaded documents
      const hasDescription = projectDescription && projectDescription.trim().length > 0;
      const hasDocuments = selectedDocuments.length > 0;
      const hasExistingDocuments = existingProjectDocuments.length > 0;
      
      if (!hasDescription && !hasDocuments && !hasExistingDocuments) {
        setToast({
          type: 'error',
          message: 'Please either provide a project description or upload project documents. Our AI needs some understanding of your project to provide accurate analysis.'
        });
        return;
      }

      // Determine change type for reprocessing
      let changeType = 'none';
      
      // Check if SOW files changed
      const sowFilesChanged = selectedDocuments.length > 0 || deletedProjectFiles.length > 0;
      
      // Check if contractor files changed (existing contractors) OR new contractors added
      const contractorFilesChanged = Object.keys(contractorDocuments).some(key => 
        contractorDocuments[key].documents.length > 0 || 
        deletedContractorFiles.some(df => df.contractorKey === key)
      );
      
      // Check if new contractors are being added (not just editing existing ones)
      const hasNewContractors = editingComparisonId && !isEditingProject;
      
      // Check if only behavioral data changed
      const onlyBehavioralChanged = !sowFilesChanged && !contractorFilesChanged && !hasNewContractors &&
        Object.keys(contractorDocuments).some(key => {
          const contractor = contractorDocuments[key];
          return contractor.behavioralNotes || contractor.appearance > 0 || 
                 contractor.organization > 0 || contractor.competency > 0;
        });
      
      // Determine the appropriate change type
      if (sowFilesChanged && (contractorFilesChanged || hasNewContractors)) {
        changeType = 'full'; // SOW changed + contractor changes → Step 1
      } else if (sowFilesChanged) {
        changeType = 'sow_only'; // Only SOW changed → Step 1
      } else if (contractorFilesChanged || hasNewContractors) {
        changeType = 'contractor_only'; // Only contractor changes → Step 2
      } else if (onlyBehavioralChanged) {
        changeType = 'behavioral_only'; // Only behavioral data → evaluation only
      }

      console.log('🔍 Change detection:', {
        sowFilesChanged,
        contractorFilesChanged,
        hasNewContractors,
        onlyBehavioralChanged,
        finalChangeType: changeType
      });

      // Prepare updated contractors data
      let updatedContractors;
      
      if (hasNewContractors) {
        // Adding new contractors to existing project
        const existingContractors = addedContractors || [];
        const newContractors = [];
        
        Object.entries(contractorDocuments).forEach(([key, contractorData]) => {
          if (contractorData.documents && contractorData.documents.length > 0) {
            const newContractorIndex = newContractors.length + 1;
            newContractors.push({
              name: `Contractor ${existingContractors.length + newContractorIndex}`,
              email: '',
              phone: '',
              company: '',
              behavioralNotes: contractorData.behavioralNotes || '',
              appearance: contractorData.appearance || 0,
              organization: contractorData.organization || 0,
              competency: contractorData.competency || 0,
              documents: contractorData.documents,
              folderName: `contractor_${existingContractors.length + newContractorIndex}`
            });
          }
        });
        
        // Combine existing + new contractors for database
        updatedContractors = [
          ...existingContractors.map(contractor => ({
            name: contractor.name,
            email: contractor.email,
            phone: contractor.phone,
            company: contractor.company,
            behavioralNotes: contractor.behavioralNotes,
            appearance: contractor.appearance,
            organization: contractor.organization,
            competency: contractor.competency
          })),
          ...newContractors.map(contractor => ({
            name: contractor.name,
            email: contractor.email,
            phone: contractor.phone,
            company: contractor.company,
            behavioralNotes: contractor.behavioralNotes,
            appearance: contractor.appearance,
            organization: contractor.organization,
            competency: contractor.competency
          }))
        ];
      } else {
        // Regular edit mode - map existing contractor keys
        updatedContractors = contractorKeys.map((key, index) => {
          const contractorData = contractorDocuments[key];
          return {
            contractorKey: key,
            documents: contractorData.documents,
            behavioralNotes: contractorData.behavioralNotes || '',
            appearance: contractorData.appearance || 0,
            organization: contractorData.organization || 0,
            competency: contractorData.competency || 0
          };
        });
      }

      // Prepare update data (without file data - files are handled separately)
      const updateData = {
        projectName: projectName,
        projectDescription: projectDescription,
        projectLocation: projectLocation,
        projectBudget: projectBudget,
        contractors: updatedContractors,
        changeType: changeType // Pass the determined change type to backend
      };

      console.log('Sending update data:', updateData);

      // First, update the comparison metadata
      const response = await proposalComparisonService.updateComparison(editingComparisonId, updateData);

      if (response.data && response.data.status === 'success') {
        
        // Now handle file uploads separately
        const uploadPromises = [];
        
        // Upload new project documents (SOW files) if any
        if (selectedDocuments.length > 0) {
          const projectFormData = new FormData();
          projectFormData.append('projectName', projectName);
          
          for (let i = 0; i < selectedDocuments.length; i++) {
            projectFormData.append('files', selectedDocuments[i]);
          }
          
          uploadPromises.push(
            proposalComparisonService.uploadContractorFiles(editingComparisonId, "project", projectFormData)
              .then(response => {
                console.log(`✅ Project documents uploaded successfully:`, response);
                return response;
              })
              .catch(error => {
                console.error(`❌ Failed to upload project documents:`, error);
                return null;
              })
          );
        }
        
        // Upload new contractor documents if any
        console.log('🔍 DEBUG: Checking contractor documents for upload:', contractorDocuments);
        
        if (hasNewContractors) {
          // Handle new contractors being added
          const existingContractorCount = addedContractors ? addedContractors.length : 0;
          let newContractorIndex = 0;
          
          for (const [contractorKey, contractorData] of Object.entries(contractorDocuments)) {
            if (contractorData.documents && contractorData.documents.length > 0) {
              newContractorIndex++;
              const contractorFolder = `contractor_${existingContractorCount + newContractorIndex}`;
              
              console.log(`📤 Uploading ${contractorData.documents.length} files for new contractor: ${contractorFolder}`);
              
              const contractorFormData = new FormData();
              contractorFormData.append('projectName', projectName);
              contractorFormData.append('contractor', `Contractor ${existingContractorCount + newContractorIndex}`);
              contractorFormData.append('company', '');
              
              for (let i = 0; i < contractorData.documents.length; i++) {
                contractorFormData.append('files', contractorData.documents[i]);
                console.log(`📎 Added file ${i + 1}: ${contractorData.documents[i].name}`);
              }
              
              uploadPromises.push(
                proposalComparisonService.uploadContractorFiles(editingComparisonId, contractorFolder, contractorFormData)
                  .then(response => {
                    console.log(`✅ Files uploaded successfully for ${contractorFolder}:`, response);
                    return response;
                  })
                  .catch(error => {
                    console.error(`❌ Failed to upload files for ${contractorFolder}:`, error);
                    return null;
                  })
              );
            }
          }
        } else {
          // Handle regular edit mode (existing contractors)
          for (const [contractorKey, contractorData] of Object.entries(contractorDocuments)) {
            console.log(`🔍 DEBUG: Processing ${contractorKey}:`, contractorData);
            console.log(`🔍 DEBUG: Documents count for ${contractorKey}:`, contractorData.documents?.length || 0);
            
            if (contractorData.documents && contractorData.documents.length > 0) {
              console.log(`📤 Uploading ${contractorData.documents.length} files for ${contractorKey}`);
              
              const contractorFormData = new FormData();
              contractorFormData.append('projectName', projectName);
              
              for (let i = 0; i < contractorData.documents.length; i++) {
                contractorFormData.append('files', contractorData.documents[i]);
                console.log(`📎 Added file ${i + 1}: ${contractorData.documents[i].name}`);
              }
              
              // Map contractorKey to the correct folder name (contractor1 -> contractor_1)
              const contractorIndex = contractorKeys.indexOf(contractorKey);
              const contractorFolder = `contractor_${contractorIndex + 1}`;
              
              console.log(`🗂️ Mapping ${contractorKey} (index ${contractorIndex}) to folder: ${contractorFolder}`);
              
              uploadPromises.push(
                proposalComparisonService.uploadContractorFiles(editingComparisonId, contractorFolder, contractorFormData)
                  .then(response => {
                    console.log(`✅ Files uploaded successfully for ${contractorKey}:`, response);
                    return response;
                  })
                  .catch(error => {
                    console.error(`❌ Failed to upload files for ${contractorKey}:`, error);
                    return null;
                  })
              );
            } else {
              console.log(`⏭️ Skipping ${contractorKey}: no documents to upload`);
            }
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          console.log(`📤 Uploading ${uploadPromises.length} file sets...`);
          await Promise.all(uploadPromises);
          console.log(`✅ All file uploads completed`);
        }

        // Show success toast and close modal immediately
        setToast({
          type: 'success',
          message: `🌟 Premium Analysis Started! ${response.data.message || 'You will receive an email notification when your detailed analysis is complete (typically 3-5 minutes).'}`
        });

        // Reset editing state
        setEditingComparisonId(null);
        setIsEditingProject(false);
        setSelectedDocuments([]);
        setContractorDocuments({
          contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
          contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
        });
        setDeletedProjectFiles([]);
        setDeletedContractorFiles([]);
        
        // Reset file input refs
        if (projectFileInputRef.current) {
          projectFileInputRef.current.value = '';
        }
        Object.keys(contractorFileInputRefs.current).forEach(key => {
          if (contractorFileInputRefs.current[key]) {
            contractorFileInputRefs.current[key].value = '';
          }
        });
        
        // Close modal and refresh the project list
        setShowCreateModal(false);
        resetForm();
        
        // Refresh the comparisons list
        const updatedComparisons = await proposalComparisonService.getUserComparisons();
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            hasPremiumAnalysis: comparison.has_premium_analysis || false,
            data: comparison
          })));
          
          // Trigger refresh
          setRefreshTrigger(prev => prev + 1);
        }
        
      } else {
        setToast({
          type: 'error',
          message: response.data?.message || 'Failed to save changes. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error saving project edits:', error);
      
      // Handle specific comparison limit error
      if (error.response && error.response.data && error.response.data.code === 'COMPARISON_LIMIT_EXCEEDED') {
        setToast({
          type: 'error',
          message: error.response.data.message
        });
        
        // Navigate to pricing page after a short delay
        setTimeout(() => {
          navigate('/homeowner/pricing');
        }, 3000);
      } else {
        setToast({
          type: 'error',
          message: error.response?.data?.message || 'An error occurred while saving. Please try again.'
        });
      }
    }
  };

  // Handle delete project
  const handleDeleteProject = (comparison) => {
    setDeletingComparison(comparison);
    setShowDeleteModal(true);
  };

  // Confirm delete project
  const confirmDeleteProject = async () => {
    if (!deletingComparison) return;
    
    try {
      setIsDeleting(true);
      
      const response = await proposalComparisonService.deleteComparison(deletingComparison.id);
      
      if (response.data && response.data.status === 'success') {
        setToast({
          type: 'success',
          message: 'Project deleted successfully!'
        });
        
        // Remove the deleted comparison from the list
        setComparisons(prevComparisons => 
          prevComparisons.filter(comp => comp.id !== deletingComparison.id)
        );
        
        // Reset pagination if needed
        const remainingComparisons = comparisons.filter(comp => comp.id !== deletingComparison.id);
        const newTotalPages = getTotalPages(remainingComparisons, itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
        
        // Trigger refresh of plan usage data in navbar
        window.dispatchEvent(new CustomEvent('refreshPlanUsage'));
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to delete project'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeletingComparison(null);
    }
  };

  // Cancel delete project
  const cancelDeleteProject = () => {
    setShowDeleteModal(false);
    setDeletingComparison(null);
  };

  // Handle questionnaire completion
  const handleQuestionnaireComplete = (preferences) => {
    setShowQuestionnaire(false);
    setToast({
      type: 'success',
      message: 'Preferences saved! We\'ll use these to better match you with contractors.'
    });
  };

  // Handle questionnaire close (skip or close button)
  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
  };

  // Handle preferences modal
  const handleOpenPreferences = () => {
    if (userPlan === 'free') {
      setToast({
        type: 'info',
        message: 'Preferences are available with Essential+ plans. Upgrade to customize your AI analysis settings!'
      });
      setTimeout(() => navigate('/homeowner/pricing'), 2000);
      return;
    }
    setShowPreferencesModal(true);
  };

  const handleClosePreferences = () => {
    setShowPreferencesModal(false);
  };

  // Import confirmation modal handlers
  const handleImportWithPremiumAnalysis = async (selectedSessions = null) => {
    if (!pendingMigrationData) return;
    
    try {
      const { userId, guestSessions } = pendingMigrationData;
      
      // Use selected sessions if provided, otherwise use all sessions
      const sessionsToImport = selectedSessions || guestSessions;
      const comparisonIds = sessionsToImport.map(session => session.comparisonId).filter(id => id);
      
      if (comparisonIds.length === 0) {
        setToast({
          type: 'error',
          message: 'No valid projects selected for import.'
        });
        return;
      }
      
      console.log(`Importing ${comparisonIds.length} projects with premium analysis`);
      
      const response = await proposalComparisonService.migrateSpecificComparisons(comparisonIds, userId, true);
      const result = response.data;
      
      if (result.status === 'success') {
        console.log('Import with premium analysis successful:', result);
        
        // Remove the pending migration data
        localStorage.removeItem('pendingGuestMigration');
        
        // Clean up guest sessions from frontend storage
        for (const session of sessionsToImport) {
          guestSessionService.deleteGuestSession(session.id);
          console.log(`Removed frontend session: ${session.id}`);
        }
        
        // Clean up guest session data if all sessions are migrated
        const remainingSessions = guestSessionService.getGuestSessions();
        if (remainingSessions.length === 0) {
          localStorage.removeItem('guestSessionId');
          sessionStorage.removeItem('guestSessionId');
          localStorage.removeItem('guestSessionCreated');
        }
        
        // Show success toast
        const migratedCount = result.data?.migratedComparisons || 0;
        if (migratedCount > 0) {
          setToast({
            type: 'success',
            message: `Successfully imported ${migratedCount} guest project${migratedCount !== 1 ? 's' : ''} with premium analysis! You'll receive an email when the analysis is complete.`
          });
        } else if (comparisonIds.length > 0) {
          setToast({
            type: 'info',
            message: 'Guest projects were processed successfully. Some may have already been in your account.'
          });
        }
        
        // Refresh comparisons and update limits
        await refreshComparisonsAndLimits();
        
      } else {
        throw new Error(result.message || 'Failed to import guest projects');
      }
    } catch (error) {
      console.error('Error importing with premium analysis:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'There was an issue importing your guest projects. Please try again later.'
      });
    } finally {
      setShowImportConfirmationModal(false);
      setPendingMigrationData(null);
    }
  };

  const handleImportOnly = async (selectedSessions = null) => {
    if (!pendingMigrationData) return;
    
    try {
      const { userId, guestSessions } = pendingMigrationData;
      
      // Use selected sessions if provided, otherwise use all sessions
      const sessionsToImport = selectedSessions || guestSessions;
      const comparisonIds = sessionsToImport.map(session => session.comparisonId).filter(id => id);
      
      if (comparisonIds.length === 0) {
        setToast({
          type: 'error',
          message: 'No valid projects selected for import.'
        });
        return;
      }
      
      console.log(`Importing ${comparisonIds.length} projects without premium analysis`);
      
      const response = await proposalComparisonService.migrateSpecificComparisons(comparisonIds, userId, false);
      const result = response.data;
      
      if (result.status === 'success') {
        console.log('Import only successful:', result);
        
        // Remove the pending migration data
        localStorage.removeItem('pendingGuestMigration');
        
        // Clean up guest sessions from frontend storage
        for (const session of sessionsToImport) {
          guestSessionService.deleteGuestSession(session.id);
          console.log(`Removed frontend session: ${session.id}`);
        }
        
        // Clean up guest session data if all sessions are migrated
        const remainingSessions = guestSessionService.getGuestSessions();
        if (remainingSessions.length === 0) {
          localStorage.removeItem('guestSessionId');
          sessionStorage.removeItem('guestSessionId');
          localStorage.removeItem('guestSessionCreated');
        }
        
        // Show success toast
        const migratedCount = result.data?.migratedComparisons || 0;
        if (migratedCount > 0) {
          setToast({
            type: 'success',
            message: `Successfully imported ${migratedCount} guest project${migratedCount !== 1 ? 's' : ''} to your account! You can run premium analysis later when you're ready.`
          });
        } else if (comparisonIds.length > 0) {
          setToast({
            type: 'info',
            message: 'Guest projects were processed successfully. Some may have already been in your account.'
          });
        }
        
        // Refresh comparisons and update limits
        await refreshComparisonsAndLimits();
        
      } else {
        throw new Error(result.message || 'Failed to import guest projects');
      }
    } catch (error) {
      console.error('Error importing without premium analysis:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'There was an issue importing your guest projects. Please try again later.'
      });
    } finally {
      setShowImportConfirmationModal(false);
      setPendingMigrationData(null);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportConfirmationModal(false);
    setPendingMigrationData(null);
  };

  const handleRunPremiumAnalysis = async (comparison) => {
    try {
      setToast({
        type: 'info',
        message: 'Starting premium analysis... This may take a few minutes.'
      });

      // Add to processing set
      setProcessingComparisons(prev => new Set(prev).add(comparison.id));

      // Call the API to run premium analysis
      const response = await proposalComparisonService.upgradeProjectToPremium(comparison.id);
      
      if (response.data.status === 'success') {
        setToast({
          type: 'success',
          message: 'Premium analysis started successfully! You\'ll receive an email when it\'s complete.'
        });
        
        // Refresh the comparisons to update the UI
        await refreshComparisonsAndLimits();
      } else {
        throw new Error(response.data.message || 'Failed to start premium analysis');
      }
    } catch (error) {
      console.error('Error running premium analysis:', error);
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'Failed to start premium analysis. Please try again.'
      });
      
      // Remove from processing set on error
      setProcessingComparisons(prev => {
        const newSet = new Set(prev);
        newSet.delete(comparison.id);
        return newSet;
      });
    }
  };

  // Check if a comparison is currently being processed
  const isComparisonProcessing = (comparisonId) => {
    return processingComparisons.has(comparisonId);
  };

  // Check background process status for all comparisons
  const checkAllComparisonStatuses = async () => {
    try {
      const processingSet = new Set();
      
      for (const comparison of comparisons) {
        if (!comparison.hasPremiumAnalysis) {
          try {
            const response = await proposalComparisonService.checkPremiumAnalysisStatus(comparison.id);
            if (response.data.status === 'success' && response.data.data.activeProcesses > 0) {
              processingSet.add(comparison.id);
            }
          } catch (error) {
            console.error(`Error checking status for comparison ${comparison.id}:`, error);
          }
        }
      }
      
      setProcessingComparisons(processingSet);
    } catch (error) {
      console.error('Error checking comparison statuses:', error);
    }
  };

  // Helper function to refresh comparisons and limits
  const refreshComparisonsAndLimits = async () => {
    try {
      const [updatedComparisons, updatedUserStats] = await Promise.all([
        proposalComparisonService.getUserComparisons(),
        userService.getUserStats()
      ]);
      
      if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
        const mappedComparisons = updatedComparisons.data.data.map(comparison => ({
          id: comparison.id,
          name: comparison.project_name,
          description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
          createdAt: comparison.created_at,
          contractors: (comparison.contractors || []).length,
          hasPremiumAnalysis: comparison.has_premium_analysis || false,
          data: comparison
        }));
        setComparisons(mappedComparisons);
        
        // Check background process status for all comparisons after refresh
        await checkAllComparisonStatuses();
      }
      
      // Update comparison limits after migration
      if (updatedUserStats.data && updatedUserStats.data.status === 'success') {
        const newCurrentCount = updatedUserStats.data.data.proposalComparisons || 0;
        setComparisonLimit(prev => ({
          ...prev,
          current: newCurrentCount
        }));
      }
      
      // Trigger refresh of plan usage data in navbar
      window.dispatchEvent(new CustomEvent('refreshPlanUsage'));
    } catch (error) {
      console.error('Error refreshing comparisons after import:', error);
    }
  };

  // Get uploaded contractors count
  const getUploadedContractorsCount = () => {
    if (isEditingProject) {
      // When editing, count contractors that either have new documents OR existing documents
      return contractorKeys.filter(contractorKey => {
        const contractor = contractorDocuments[contractorKey];
        const hasNewDocuments = contractor?.documents.length > 0;
        const hasExistingDocuments = existingContractorDocuments[contractorKey] && 
          existingContractorDocuments[contractorKey].length > 0;
        return hasNewDocuments || hasExistingDocuments;
      }).length;
    } else if (editingComparisonId && !isEditingProject) {
      // When adding contractors to existing project, count existing contractors + new contractors with documents
      const existingCount = addedContractors.length;
      const newContractorsWithDocs = Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
      return existingCount + newContractorsWithDocs;
    } else {
      // When creating new, only count contractors with new documents
      return Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
    }
  };

  // Add new contractor section
  const addContractorSection = () => {
    const currentPlanFeatures = getCurrentPlanFeatures();
    
    // Calculate current total contractors (existing + new)
    const existingCount = editingComparisonId && !isEditingProject ? addedContractors.length : 0;
    const currentTotal = existingCount + contractorKeys.length;
    const maxAllowed = currentPlanFeatures.maxContractors;
    
    if (currentTotal >= maxAllowed) return;
    
    const nextContractorNumber = currentTotal + 1;
    const newContractorKey = `contractor${nextContractorNumber}`;
    
    setContractorKeys(prev => [...prev, newContractorKey]);
    setContractorDocuments(prev => ({
      ...prev,
      [newContractorKey]: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
    }));
  };

  // Remove contractor section
  const removeContractorSection = (contractorKey) => {
    const currentPlanFeatures = getCurrentPlanFeatures();
    if (contractorKeys.length <= 2) return;
    
    setContractorKeys(prev => prev.filter(key => key !== contractorKey));
    setContractorDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[contractorKey];
      return newDocs;
    });
    setCollapsedContractors(prev => {
      const newCollapsed = { ...prev };
      delete newCollapsed[contractorKey];
      return newCollapsed;
    });
  };

  // Toggle contractor section collapse
  const toggleContractorCollapse = (contractorKey) => {
    setCollapsedContractors(prev => ({
      ...prev,
      [contractorKey]: !prev[contractorKey]
    }));
  };

  // Render the create comparison modal
  const renderCreateModal = () => {
    return (
      <div className={showCreateModal ? styles.modalOverlay : styles.hidden}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>{isEditingProject ? 'Edit Project' : 'Create New Comparison'}</h2>
            <button 
              className={styles.closeButton} 
              onClick={isEditingProject ? handleCancelEdit : handleCloseModal}
              aria-label="Close modal"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className={styles.modalContent}>
            {currentStep === 1 ? (
              // Step 1: Project Details
              <div className={styles.formContainer}>
                <div className={styles.formGroup}>
                  <label htmlFor="projectName" className={styles.fieldLabel}>
                    <div className={styles.labelContent}>
                      <i className="fas fa-home"></i>
                      <span>Project Name*</span>
                      <div className={styles.fieldTooltip}>
                        <i className="fas fa-info-circle"></i>
                        <div className={styles.tooltipContent}>
                          A clear project name helps our AI categorize and analyze contractor proposals more accurately
                        </div>
                      </div>
                    </div>
                  </label>
                  <input
                    type="text"
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(validateProjectName(e.target.value))}
                    placeholder="Enter project name"
                    required
                    disabled={isEditingProject}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="projectDescription" className={styles.fieldLabel}>
                    <div className={styles.labelContent}>
                      <i className="fas fa-edit"></i>
                      <span>Project Description*</span>
                      <span className={styles.requiredAsterisk}>(Required if no documents uploaded)</span>
                      <div className={styles.fieldTooltip}>
                        <i className="fas fa-info-circle"></i>
                        <div className={styles.tooltipContent}>
                          Either provide a detailed description OR upload project documents. Our AI needs to understand your project to provide accurate contractor comparison.
                        </div>
                      </div>
                    </div>
                  </label>
                  <div className={styles.textareaWrapper}>
                    <textarea
                      id="projectDescription"
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Describe your project or tap the microphone to use voice input"
                      rows={4}
                    />
                    <button 
                      type="button"
                      className={`${styles.voiceButton} ${isRecording ? styles.recording : ''}`}
                      onClick={toggleRecording}
                      title={isRecording ? "Stop recording" : "Start voice input"}
                    >
                      <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
                    </button>
                  </div>
                  {isRecording && (
                    <div className={styles.recordingIndicator}>
                      <div className={styles.pulse}></div>
                      <span>Recording... Speak now</span>
                    </div>
                  )}
                </div>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="projectLocation" className={styles.fieldLabel}>
                      <div className={styles.labelContent}>
                        <i className="fas fa-map-marker-alt"></i>
                        <span>Location (Zip Code)</span>
                        <div className={styles.fieldTooltip}>
                          <i className="fas fa-info-circle"></i>
                          <div className={styles.tooltipContent}>
                            Location helps our AI analyze if contractor pricing is reasonable for your area and if they understand local building codes
                          </div>
                        </div>
                      </div>
                    </label>
                    <input
                      type="text"
                      id="projectLocation"
                      value={projectLocation}
                      onChange={(e) => setProjectLocation(e.target.value)}
                      placeholder="Enter zip code"
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="projectBudget" className={styles.fieldLabel}>
                      <div className={styles.labelContent}>
                        <i className="fas fa-dollar-sign"></i>
                        <span>Project Budget</span>
                        <div className={styles.fieldTooltip}>
                          <i className="fas fa-info-circle"></i>
                          <div className={styles.tooltipContent}>
                            Your budget helps our AI evaluate which contractor proposals offer the best value and identify any overpriced or suspiciously low bids
                          </div>
                        </div>
                      </div>
                    </label>
                    <input
                      type="text"
                      id="projectBudget"
                      value={projectBudget}
                      onChange={(e) => setProjectBudget(e.target.value)}
                      placeholder="Enter budget amount"
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>
                    <div className={styles.labelContent}>
                      <i className="fas fa-file-upload"></i>
                      <span>Project Documents*</span>
                      <span className={styles.requiredAsterisk}>(Required if no description provided)</span>
                      <div className={styles.fieldTooltip}>
                        <i className="fas fa-info-circle"></i>
                        <div className={styles.tooltipContent}>
                          Either upload project documents OR provide a detailed description. Our AI needs to understand your project to provide accurate contractor comparison.
                        </div>
                      </div>
                    </div>
                  </label>
                  <div className={styles.fileUploadContainer}>
                    <input
                      type="file"
                      id="projectDocuments"
                      onChange={handleDocumentUpload}
                      multiple
                      className={styles.fileInput}
                    />
                    <label htmlFor="projectDocuments" className={styles.fileInputLabel}>
                      <span>{isEditingProject ? "Add More Files" : "Choose Files"}</span>
                    </label>
                  </div>
                  
                  {selectedDocuments.length > 0 && (
                    <div className={styles.selectedFiles}>
                      <h4>New Documents to Upload:</h4>
                      <ul>
                        {selectedDocuments.map((doc, index) => (
                          <li key={index}>
                            {doc.name}
                            <button
                              className={styles.removeButton}
                              onClick={() => removeDocument(index)}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {isEditingProject && existingProjectDocuments.length > 0 && (
                    <div className={styles.existingFiles}>
                      <h4>Existing Documents:</h4>
                      <ul>
                        {existingProjectDocuments.map((doc) => (
                          <li key={doc.id} className={styles.existingFileItem}>
                            <span className={styles.fileName}>
                              {doc.original_name || doc.file_name}
                            </span>
                            <div className={styles.fileControls}>
                              <button 
                                className={styles.fileActionButton}
                                onClick={() => {
                                  const fileUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/${doc.file_path.replace(/\\/g, '/').replace(/^uploads[\/\\]/, '')}`;
                                  window.open(fileUrl, '_blank');
                                }}
                                title="View Document"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button 
                                className={`${styles.fileActionButton} ${styles.deleteButton}`}
                                onClick={() => handleDeleteExistingDocument(doc.id, doc.original_name, 'project')}
                                title="Delete Document"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Step 2: Contractor Information
              <div>
                {/* Simple Contractor Upload Grid */}
                <div className={styles.contractorForm}>
                  <div className={styles.contractorFormHeader}>
                    <div>
                      <h3>Upload Contractor Proposals</h3>
                      <p>Upload proposals and add notes for up to {getCurrentPlanFeatures().maxContractors} contractors. {getCurrentPlanFeatures().hasBehavioralNotes ? 'Add behavioral notes and ratings for each.' : 'Upgrade to Essential+ for behavioral notes and ratings.'}</p>
                    </div>
                  </div>
                  
                  {/* Contractor Upload Sections */}
                  <div className={styles.contractorUploadGrid}>
                    {/* Show existing contractors in summary format when adding contractors */}
                    {editingComparisonId && !isEditingProject && addedContractors.length > 0 && (
                      <div className={styles.existingContractorsSection}>
                        <h4>Existing Contractors</h4>
                        <div className={styles.existingContractorsList}>
                          {addedContractors.map((contractor, index) => (
                            <div key={`existing-${index}`} className={styles.existingContractorSummary}>
                              <div className={styles.contractorSummaryHeader}>
                                <div className={styles.contractorNumber}>
                                  {index + 1}
                                </div>
                                <div className={styles.contractorSummaryInfo}>
                                  <h5>{contractor.name || `Contractor ${index + 1}`}</h5>
                                  {contractor.company && (
                                    <p className={styles.contractorCompany}>{contractor.company}</p>
                                  )}
                                </div>
                                <div className={styles.contractorSummaryStatus}>
                                  <i className="fas fa-check-circle" style={{color: '#10b981'}}></i>
                                  <span>Files uploaded</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Show new contractor sections */}
                    {contractorKeys.map((contractorKey, index) => {
                      // Calculate the actual contractor number (existing + new)
                      const contractorNumber = editingComparisonId && !isEditingProject 
                        ? addedContractors.length + index + 1 
                        : index + 1;
                      
                      return (
                        <div key={contractorKey} className={`${styles.contractorUploadCard} ${collapsedContractors[contractorKey] ? styles.collapsed : ''}`}>
                          <div className={styles.contractorUploadHeader}>
                            <div className={styles.contractorHeaderLeft}>
                              <div className={styles.contractorNumber}>
                                {contractorNumber}
                              </div>
                              <div>
                                <h4>
                                  {isEditingProject && contractorDocuments[contractorKey]?.existingName 
                                    ? contractorDocuments[contractorKey].existingName 
                                    : `Contractor ${contractorNumber}`}
                                </h4>
                                {isEditingProject && contractorDocuments[contractorKey]?.existingCompany && (
                                  <p style={{margin: 0, fontSize: '0.85rem', color: '#64748b'}}>
                                    {contractorDocuments[contractorKey].existingCompany}
                                  </p>
                                )}
                              </div>
                              {contractorDocuments[contractorKey]?.documents.length > 0 && (
                                <div className={styles.uploadedBadge}>
                                  <i className="fas fa-check-circle"></i>
                                  {contractorDocuments[contractorKey].documents.length} new file(s)
                                </div>
                              )}
                            </div>
                            <div className={styles.contractorHeaderActions}>
                              <button
                                type="button"
                                className={styles.collapseButton}
                                onClick={() => toggleContractorCollapse(contractorKey)}
                                title={collapsedContractors[contractorKey] ? "Expand" : "Collapse"}
                              >
                                <i className={`fas ${collapsedContractors[contractorKey] ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
                              </button>
                              {contractorKeys.length > 1 && (
                                <button
                                  type="button"
                                  className={styles.removeContractorButton}
                                  onClick={() => removeContractorSection(contractorKey)}
                                  title="Remove Contractor"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {!collapsedContractors[contractorKey] && (
                            <div className={styles.contractorContent}>
                              <div className={styles.fileUploadContainer}>
                                <input
                                  type="file"
                                  id={`${contractorKey}Documents`}
                                  onChange={(e) => handleContractorFileUpload(contractorKey, e)}
                                  multiple
                                  className={styles.fileInput}
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                />
                                <label htmlFor={`${contractorKey}Documents`} className={styles.fileUploadDropZone}>
                                  <div className={styles.uploadIcon}>
                                    <i className="fas fa-cloud-upload-alt"></i>
                                  </div>
                                  <div className={styles.uploadText}>
                                    <span>{isEditingProject ? "Add more files or click to upload" : "Drop files here or click to upload"}</span>
                                    <small>PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
                                  </div>
                                </label>
                              </div>
                              
                              {contractorDocuments[contractorKey]?.documents.length > 0 && (
                                <div className={styles.uploadedFilesList}>
                                  {contractorDocuments[contractorKey].documents.map((file, fileIndex) => (
                                    <div key={fileIndex} className={styles.uploadedFile}>
                                      <div className={styles.fileInfo}>
                                        <i className="fas fa-file-alt"></i>
                                        <span>{file.name}</span>
                                      </div>
                                      <button
                                        className={styles.removeFileButton}
                                        onClick={() => removeContractorFile(contractorKey, fileIndex)}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Show existing contractor files when editing */}
                              {isEditingProject && existingContractorDocuments[contractorKey] && existingContractorDocuments[contractorKey].length > 0 && (
                                <div className={styles.existingFiles}>
                                  <h4>Existing Documents:</h4>
                                  <ul>
                                    {existingContractorDocuments[contractorKey].map((doc) => (
                                      <li key={doc.id} className={styles.existingFileItem}>
                                        <span className={styles.fileName}>
                                          {doc.original_name || doc.file_name}
                                        </span>
                                        <div className={styles.fileControls}>
                                          <button 
                                            className={styles.fileActionButton}
                                            onClick={() => {
                                              const fileUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/${doc.file_path.replace(/\\/g, '/').replace(/^uploads[\/\\]/, '')}`;
                                              window.open(fileUrl, '_blank');
                                            }}
                                            title="View Document"
                                          >
                                            <i className="fas fa-eye"></i>
                                          </button>
                                          <button 
                                            className={`${styles.fileActionButton} ${styles.deleteButton}`}
                                            onClick={() => handleDeleteExistingDocument(doc.id, doc.original_name, 'contractor')}
                                            title="Delete Document"
                                          >
                                            <i className="fas fa-trash-alt"></i>
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {/* Behavioral Notes for this contractor */}
                              <div className={styles.formGroup}>
                                <label htmlFor={`behavioralNotes${index}`} className={styles.fieldLabel}>
                                  <div className={styles.labelContent}>
                                    <i className="fas fa-comments"></i>
                                    <span>Behavioral Notes</span>
                                    {!getCurrentPlanFeatures().hasBehavioralNotes && (
                                      <span className={styles.premiumBadge}>
                                        <i className="fas fa-crown"></i> Essential+
                                      </span>
                                    )}
                                    <div className={styles.fieldTooltip}>
                                      <i className="fas fa-info-circle"></i>
                                      <div className={styles.tooltipContent}>
                                        Add notes about contractor behavior, communication style, responsiveness, and any red flags you noticed during interactions. This helps our AI provide more personalized recommendations.
                                      </div>
                                    </div>
                                  </div>
                                </label>
                                <div className={`${styles.textareaWrapper} ${!getCurrentPlanFeatures().hasBehavioralNotes ? styles.premiumDisabled : ''}`}>
                                  <textarea
                                    id={`behavioralNotes${index}`}
                                    value={contractorDocuments[contractorKey]?.behavioralNotes || ''}
                                    onChange={getCurrentPlanFeatures().hasBehavioralNotes ? (e) => setContractorDocuments(prev => ({
                                      ...prev,
                                      [contractorKey]: {
                                        ...prev[contractorKey],
                                        behavioralNotes: e.target.value
                                      }
                                    })) : undefined}
                                    placeholder={getCurrentPlanFeatures().hasBehavioralNotes ? "Add notes about contractor behavior, communication, etc. or tap the microphone to use voice input" : "Upgrade to Essential+ to add behavioral notes"}
                                    rows={3}
                                    disabled={!getCurrentPlanFeatures().hasBehavioralNotes}
                                  />
                                  <button 
                                    type="button"
                                    className={`${styles.voiceButton} ${isBehavioralNoteRecording && activeContractorKey === contractorKey ? styles.recording : ''} ${!getCurrentPlanFeatures().hasBehavioralNotes ? styles.premiumDisabled : ''}`}
                                    onClick={getCurrentPlanFeatures().hasBehavioralNotes ? () => {
                                      setActiveContractorKey(contractorKey);
                                      toggleBehavioralNoteRecording();
                                    } : undefined}
                                    disabled={!getCurrentPlanFeatures().hasBehavioralNotes}
                                    title={!getCurrentPlanFeatures().hasBehavioralNotes ? "Upgrade to Essential+ for voice input" : (isBehavioralNoteRecording && activeContractorKey === contractorKey ? "Stop recording" : "Start voice input")}
                                  >
                                    <i className={`fas ${isBehavioralNoteRecording && activeContractorKey === contractorKey ? 'fa-stop' : 'fa-microphone'}`}></i>
                                  </button>
                                </div>
                                {isBehavioralNoteRecording && activeContractorKey === contractorKey && (
                                  <div className={styles.recordingIndicator}>
                                    <div className={styles.pulse}></div>
                                    <span>Recording... Speak now</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Contractor Perception Ratings */}
                              <div className={styles.formGroup}>
                                <label className={styles.fieldLabel}>
                                  <div className={styles.labelContent}>
                                    <i className="fas fa-star"></i>
                                    <span>Contractor Perception Ratings</span>
                                    {!getCurrentPlanFeatures().hasContractorRatings && (
                                      <span className={styles.premiumBadge}>
                                        <i className="fas fa-crown"></i> Essential+
                                      </span>
                                    )}
                                    <div className={styles.fieldTooltip}>
                                      <i className="fas fa-info-circle"></i>
                                      <div className={styles.tooltipContent}>
                                        Rate your perception of each contractor based on your interactions. These ratings help our AI understand your preferences and provide better recommendations for future projects.
                                      </div>
                                    </div>
                                  </div>
                                </label>
                                <div className={`${styles.ratingsContainer} ${!getCurrentPlanFeatures().hasContractorRatings ? styles.premiumDisabled : ''}`}>
                                  <div className={styles.ratingGroup}>
                                    <label className={styles.ratingLabel}>
                                      <span>Appearance/Professionalism</span>
                                      <div className={styles.ratingTooltip}>
                                        <i className="fas fa-info-circle"></i>
                                        <div className={styles.tooltipContent}>
                                          Rate how professional and well-presented the contractor appeared during meetings and site visits
                                        </div>
                                      </div>
                                    </label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.appearance >= star ? styles.starActive : ''}`}
                                            onClick={getCurrentPlanFeatures().hasContractorRatings ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                appearance: star
                                              }
                                            })) : undefined}
                                            disabled={!getCurrentPlanFeatures().hasContractorRatings}
                                            type="button"
                                            title={!getCurrentPlanFeatures().hasContractorRatings ? "Upgrade to Essential+ to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.appearance >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.appearance || 0}/5</span>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.ratingGroup}>
                                    <label className={styles.ratingLabel}>
                                      <span>Organization</span>
                                      <div className={styles.ratingTooltip}>
                                        <i className="fas fa-info-circle"></i>
                                        <div className={styles.tooltipContent}>
                                          Rate how well-organized and prepared the contractor was with documentation, timelines, and project planning
                                        </div>
                                      </div>
                                    </label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.organization >= star ? styles.starActive : ''}`}
                                            onClick={getCurrentPlanFeatures().hasContractorRatings ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                organization: star
                                              }
                                            })) : undefined}
                                            disabled={!getCurrentPlanFeatures().hasContractorRatings}
                                            type="button"
                                            title={!getCurrentPlanFeatures().hasContractorRatings ? "Upgrade to Essential+ to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.organization >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.organization || 0}/5</span>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.ratingGroup}>
                                    <label className={styles.ratingLabel}>
                                      <span>Competency</span>
                                      <div className={styles.ratingTooltip}>
                                        <i className="fas fa-info-circle"></i>
                                        <div className={styles.tooltipContent}>
                                          Rate how knowledgeable and technically competent the contractor appeared to be about your specific project type
                                        </div>
                                      </div>
                                    </label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.competency >= star ? styles.starActive : ''}`}
                                            onClick={getCurrentPlanFeatures().hasContractorRatings ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                competency: star
                                              }
                                            })) : undefined}
                                            disabled={!getCurrentPlanFeatures().hasContractorRatings}
                                            type="button"
                                            title={!getCurrentPlanFeatures().hasContractorRatings ? "Upgrade to Essential+ to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.competency >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.competency || 0}/5</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Add Contractor Button - below contractor sections */}
                  {(() => {
                    const existingCount = editingComparisonId && !isEditingProject ? addedContractors.length : 0;
                    const currentTotal = existingCount + contractorKeys.length;
                    const currentPlanFeatures = getCurrentPlanFeatures();
                    const maxAllowed = currentPlanFeatures.maxContractors;
                    const canAddMore = currentTotal < maxAllowed;
                    const isAtFreeTierLimit = userPlan === 'free' && currentTotal >= currentPlanFeatures.maxContractors;
                    
                    return canAddMore && (
                      <div className={styles.addContractorSection}>
                        <button 
                          type="button"
                          className={`${styles.addContractorButton} ${isAtFreeTierLimit ? styles.premiumDisabled : ''}`}
                          onClick={!isAtFreeTierLimit ? addContractorSection : () => navigate('/homeowner/pricing')}
                          title={isAtFreeTierLimit ? `Upgrade to Essential+ to add more contractors (${maxAllowed} max on current plan)` : "Add Contractor"}
                        >
                          {isAtFreeTierLimit && <i className="fas fa-crown" style={{color: '#fbbf24', marginRight: '0.5rem'}}></i>}
                          <i className="fas fa-plus"></i> {isAtFreeTierLimit ? 'Upgrade to Add More' : 'Add Contractor'}
                        </button>
                      </div>
                    );
                  })()}
                  
                  <div className={styles.uploadSummary}>
                    <span>Contractors with proposals: <strong>{getUploadedContractorsCount()}/{editingComparisonId && !isEditingProject ? addedContractors.length + contractorKeys.length : contractorKeys.length}</strong></span>
                    <span className={styles.minimumNote}>
                      {(() => {
                        const currentPlanFeatures = getCurrentPlanFeatures();
                        const minContractors = 2; // Always require minimum 2 contractors
                        
                        if (isEditingProject) {
                          return 'Ensure at least two contractors have documents (existing or new) to save changes';
                        } else if (editingComparisonId && !isEditingProject) {
                          return `Add documents for new contractors (minimum ${Math.max(0, minContractors - addedContractors.length)} more needed)`;
                        } else {
                          return `Upload documents for at least two contractors to proceed (${currentPlanFeatures.maxContractors} max on ${userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} plan)`;
                        }
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className={styles.modalFooter}>
            {currentStep > 1 && !editingComparisonId && !isEditingProject && (
              <button className={styles.secondaryButton} onClick={handlePreviousStep}>
                <i className="fas fa-arrow-left"></i> Back to Project Details
              </button>
            )}
            
            {isEditingProject && currentStep === 1 ? (
              <button className={styles.primaryButton} onClick={() => setCurrentStep(2)}>
                Next: Edit Contractors <i className="fas fa-arrow-right"></i>
              </button>
            ) : currentStep === 1 && !editingComparisonId && !isEditingProject ? (
              <button className={styles.primaryButton} onClick={handleNextStep}>
                Next: Add Contractors <i className="fas fa-arrow-right"></i>
              </button>
            ) : (
              <button 
                className={styles.primaryButton} 
                onClick={
                  isEditingProject 
                    ? handleSaveProjectEdits 
                    : editingComparisonId 
                      ? () => handleAddContractorToExisting(editingComparisonId) 
                      : handleSubmit
                }
                disabled={loading || (() => {
                  const minContractors = 2; // Always require minimum 2 contractors
                  
                  if (isEditingProject) {
                    return getUploadedContractorsCount() < minContractors;
                  } else if (editingComparisonId && !isEditingProject) {
                    // When adding contractors, ensure we have at least the minimum total
                    const existingCount = addedContractors.length;
                    const newContractorsWithDocs = Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
                    const totalContractors = existingCount + newContractorsWithDocs;
                    return totalContractors < minContractors || newContractorsWithDocs === 0;
                  } else {
                    return getUploadedContractorsCount() < minContractors; // Changed from === 0 to < 2
                  }
                })()}
              >
                {loading 
                  ? 'Submitting...' 
                  : isEditingProject 
                    ? 'Save Changes'
                    : editingComparisonId 
                      ? `Add ${getUploadedContractorsCount()} Contractor${getUploadedContractorsCount() !== 1 ? 's' : ''} to Project`
                      : `Create Project with ${getUploadedContractorsCount()} Contractor${getUploadedContractorsCount() !== 1 ? 's' : ''}`
                }
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add a useEffect for mobile optimization
  useEffect(() => {
    // Set meta viewport for better mobile display
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0';
    document.head.appendChild(viewportMeta);

    return () => {
      // Clean up when component unmounts
      document.head.removeChild(viewportMeta);
    };
  }, []);

  // Main component rendering
  return (
    <div className={styles.dashboardContainer}>


      {/* Personality Questionnaire for first-time Essential+ users only */}
      {showQuestionnaire && userPlan !== 'free' && (
        <PersonalityQuestionnaire
          onComplete={handleQuestionnaireComplete}
          onClose={handleQuestionnaireClose}
        />
      )}

      {/* Preferences Modal */}
      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={handleClosePreferences}
      />

      {/* Plan Usage Modal */}
      <PlanUsageModal
        isOpen={showPlanUsageModal}
        onClose={() => setShowPlanUsageModal(false)}
        subscriptionData={subscriptionData}
      />

      {/* Import Confirmation Modal */}
      <ImportConfirmationModal
        isOpen={showImportConfirmationModal}
        onClose={handleCloseImportModal}
        onConfirm={handleImportWithPremiumAnalysis}
        onImportOnly={handleImportOnly}
        isPremiumUser={userPlan !== 'free'}
        currentComparisons={comparisonLimit.current}
        maxComparisons={comparisonLimit.max}
        guestSessions={pendingMigrationData?.guestSessions || []}
      />

      {toast && <Toast 
        type={toast.type} 
        message={toast.message} 
        onClose={() => setToast(null)}
        className={styles.toastContainer}
      />}
      
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>Home Owner Dashboard</h1>
          <div className={styles.headerActions}>
            {userPlan !== 'free' ? (
              <button 
                className={styles.preferencesButton} 
                onClick={handleOpenPreferences}
                title="Manage Preferences"
              >
                <i className="fas fa-cog"></i>
                Preferences
              </button>
            ) : (
              <button 
                className={`${styles.preferencesButton} ${styles.lockedFeature}`}
                onClick={() => {
                  setToast({
                    type: 'info',
                    message: 'Preferences are available with Essential+ plans. Upgrade to customize your AI analysis settings!'
                  });
                  setTimeout(() => navigate('/homeowner/pricing'), 2000);
                }}
                title="Upgrade to Essential for Preferences"
              >
                <i className="fas fa-lock"></i>
                Preferences
                <span className={styles.essentialBadge}>Essential+</span>
              </button>
            )}
            <button 
              className={`${styles.createButton} ${comparisonLimit.current >= comparisonLimit.max ? styles.disabled : ''}`} 
              onClick={comparisonLimit.current >= comparisonLimit.max ? () => setShowPlanUsageModal(true) : handleCreateComparison}
              title={comparisonLimit.current >= comparisonLimit.max ? 
                `You've reached your limit of ${comparisonLimit.max} comparisons. Click to see upgrade options.` : 
                'Create a new comparison (costs 1 comparison)'
              }
            >
              {comparisonLimit.current >= comparisonLimit.max ? (
                <>
                  Limit Reached ({comparisonLimit.current}/{comparisonLimit.max})
                </>
              ) : (
                <>
                  Create New Comparison
                </>
              )}
            </button>
          </div>
        </div>
      </div>



      {/* Top Banner Ad - Only for free users */}
      {userPlan === 'free' && (
        <>
          {/* Real AdSense Ad - Uncomment when ready */}
          {/* <AdSenseAd
            adSlot={process.env.REACT_APP_ADSENSE_TOP_BANNER_SLOT || "your-top-banner-slot"}
            placement="banner"
            adFormat="horizontal"
          /> */}
          
          {/* Mock Ad for Testing - Remove when using real ads */}
          {!subscriptionLoading && userPlan === 'free' && (
            <MockAd placement="banner" />
          )}
        </>
      )}
      
      <div className={styles.content}>
        {(loading || subscriptionLoading) ? (
          <div className={styles.contentCard}>
            <div className={styles.loading}>
              {subscriptionLoading ? 'Loading subscription...' : 'Loading...'}
            </div>
          </div>
        ) : error ? (
          <div className={styles.contentCard}>
            <div className={styles.error}>{error}</div>
          </div>
        ) : (
          <div className={styles.contentCard}>
            <div className={styles.dashboardContent}>
              {/* Dashboard Header */}
              <div className={styles.dashboardHeader}>
                <div className={styles.dashboardTitle}>
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Your Project Comparisons
                  </h2>
                  <p>
                    {comparisons.length} project{comparisons.length !== 1 ? 's' : ''} in your portfolio
                    {comparisonLimit.max > 0 && (
                      <span 
                        className={styles.usageIndicator}
                        title="Comparison Count: Each create, edit, or delete action triggers AI comparison and costs 1 comparison. This count cannot be decreased as AI work has already been completed."
                      >
                        {' '}• {comparisonLimit.current}/{comparisonLimit.max} comparisons used
                        <i className="fas fa-info-circle" style={{ marginLeft: '4px', fontSize: '12px', opacity: 0.7 }}></i>
                        {comparisonLimit.current >= comparisonLimit.max && (
                          <span className={styles.limitReached}>
                            {' '}(Limit reached)
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
                
                {comparisons.length > 0 && (
                  <div className={styles.dashboardControls}>
                    {/* View Mode Toggle */}
                    <div className={styles.viewModeToggle}>
                      <span className={styles.controlLabel}>View:</span>
                      <div className={styles.toggleButtons}>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-1' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-1')}
                          title="List View - Detailed"
                        >
                          <i className="fas fa-list"></i>
                        </button>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-2' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-2')}
                          title="Grid View - 2 Columns"
                        >
                          <i className="fas fa-th-large"></i>
                        </button>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-3' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-3')}
                          title="Grid View - 3 Columns"
                        >
                          <i className="fas fa-th"></i>
                        </button>
                      </div>
                    </div>
                    
                    {/* Sort Options */}
                    <div className={styles.sortControls}>
                      <span className={styles.controlLabel}>Sort by:</span>
                      <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className={styles.sortSelect}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="name">Project Name</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              
              {comparisons.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateIcon}>
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                  <h3>No comparisons yet</h3>
                  <p>Create a new comparison to start evaluating contractor proposals with AI-powered insights.</p>
                  <button 
                    className={`${styles.createEmptyStateButton} ${comparisonLimit.current >= comparisonLimit.max ? styles.disabled : ''}`} 
                    onClick={comparisonLimit.current >= comparisonLimit.max ? () => setShowPlanUsageModal(true) : handleCreateComparison}
                    title={comparisonLimit.current >= comparisonLimit.max ? 
                      `You've reached your limit of ${comparisonLimit.max} comparisons. Click to see upgrade options.` : 
                      'Create your first comparison (costs 1 comparison)'
                    }
                  >
                    {comparisonLimit.current >= comparisonLimit.max ? (
                      <>
                        Limit Reached - Upgrade to Continue
                      </>
                    ) : (
                      'Create Your First Comparison'
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <div className={`${styles.comparisonGrid} ${styles[viewMode]} ${totalPages > 1 ? styles.paginated : ''}`}>
                    {paginatedComparisons.map(comparison => (
                      <div key={comparison.id} className={styles.comparisonCard}>
                        <div className={styles.cardHeader}>
                          <div className={styles.cardHeaderTop}>
                            <div className={styles.projectIconAndName}>
                              <div className={styles.projectIcon}>
                                <i className="fas fa-home"></i>
                              </div>
                                                          <div className={styles.projectNameContainer}>
                              <h3>{comparison.name}</h3>
                              {comparison.hasPremiumAnalysis ? (
                                <div className={styles.premiumAnalysisBadge} title="This project has premium AI analysis">
                                  <i className="fas fa-crown"></i>
                                  {/* <span>Premium Analysis</span> */}
                                </div>
                              ) : isComparisonProcessing(comparison.id) ? (
                                <div className={styles.processingAnalysisBadge} title="Premium analysis in progress...">
                                  <i className="fas fa-spinner fa-spin"></i>
                                  {/* <span>Processing...</span> */}
                                </div>
                              ) : (
                                <div 
                                  className={`${styles.runPremiumAnalysisBadge} ${comparisonLimit.current >= comparisonLimit.max ? styles.disabled : ''}`}
                                  title={comparisonLimit.current >= comparisonLimit.max 
                                    ? `Limit reached (${comparisonLimit.current}/${comparisonLimit.max}). Click to see upgrade options.` 
                                    : "Run premium AI analysis for enhanced insights"}
                                  onClick={comparisonLimit.current >= comparisonLimit.max 
                                    ? () => setShowPlanUsageModal(true) 
                                    : () => handleRunPremiumAnalysis(comparison)}
                                >
                                  <i className="fas fa-magic"></i>
                                  {/* <span>Run Premium Analysis</span> */}
                                </div>
                              )}
                            </div>
                            </div>
                            <div className={styles.cardMeta}>
                              <span className={styles.dateCreated}>
                                {new Date(comparison.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                              <div className={styles.contractorBadge}>
                                <i className="fas fa-users"></i>
                                {comparison.contractors} contractor{comparison.contractors !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.cardContent}>
                          <p>{comparison.description || 'No description provided'}</p>
                          
                          <div className={styles.projectStats}>
                            <div className={styles.stat}>
                              <i className="fas fa-file-contract"></i>
                              <span>Proposals</span>
                              <strong>{comparison.contractors}</strong>
                            </div>
                            {comparison.data.project_budget && (
                              <div className={styles.stat}>
                                <i className="fas fa-dollar-sign"></i>
                                <span>Budget</span>
                                <strong>${comparison.data.project_budget}</strong>
                              </div>
                            )}
                            {comparison.data.project_location && (
                              <div className={styles.stat}>
                                <i className="fas fa-map-marker-alt"></i>
                                <span>Location</span>
                                <strong>{comparison.data.project_location}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className={styles.cardFooter}>
                          <button 
                            className={styles.viewButton} 
                            onClick={() => navigate(`/homeowner/comparisons/${comparison.id}`)}
                          >
                            <i className="fas fa-chart-line"></i>
                            View Analysis
                          </button>
                          
                          <div className={styles.cardActions}>
                            <button 
                              className={`${styles.actionButton} ${comparisonLimit.current >= comparisonLimit.max ? styles.disabled : ''}`}
                              onClick={comparisonLimit.current >= comparisonLimit.max ? () => setShowPlanUsageModal(true) : () => handleEditProject(comparison)}
                              title={comparisonLimit.current >= comparisonLimit.max 
                                ? `Limit reached (${comparisonLimit.current}/${comparisonLimit.max}). Click to see upgrade options.` 
                                : "Edit Project (costs 1 comparison)"}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {/* Add Contractor Button - Only for paid users */}
                            {userPlan !== 'free' && (() => {
                              const existingContractors = comparison.data.contractors || [];
                              const existingCount = existingContractors.length;
                              const currentPlanFeatures = getCurrentPlanFeatures();
                              const maxAllowed = currentPlanFeatures.maxContractors;
                              const canAddMore = existingCount < maxAllowed;
                              const isAtComparisonLimit = comparisonLimit.current >= comparisonLimit.max;
                              
                              return canAddMore && (
                                <button 
                                  className={`${styles.actionButton} ${isAtComparisonLimit ? styles.disabled : ''}`}
                                  onClick={isAtComparisonLimit ? () => setShowPlanUsageModal(true) : () => {
                                    // Set up the form with existing project data
                                    setProjectName(comparison.name);
                                    setProjectDescription(comparison.description || '');
                                    setProjectLocation(comparison.data.project_location || '');
                                    setProjectBudget(comparison.data.project_budget || '');
                                    
                                    // Set up existing contractors data for display
                                    setAddedContractors(existingContractors);
                                    
                                    // Show at least 1 new contractor slot, but ensure we don't exceed max  
                                    const newSlotsToShow = Math.min(1, maxAllowed - existingCount);
                                    const startingContractorNumber = existingCount + 1;
                                    
                                    // Set up contractor keys for new contractors only
                                    const newContractorKeys = [];
                                    for (let i = 0; i < newSlotsToShow; i++) {
                                      newContractorKeys.push(`contractor${startingContractorNumber + i}`);
                                    }
                                    setContractorKeys(newContractorKeys);
                                    
                                    // Set up contractorDocuments state for new contractors only
                                    const contractorDocsState = {};
                                    newContractorKeys.forEach(contractorKey => {
                                      contractorDocsState[contractorKey] = {
                                        documents: [],
                                        behavioralNotes: '',
                                        appearance: 0,
                                        organization: 0,
                                        competency: 0
                                      };
                                    });
                                    setContractorDocuments(contractorDocsState);
                                    
                                    // Store the comparison ID to use when submitting
                                    setEditingComparisonId(comparison.id);
                                    
                                    // Go directly to the contractor form
                                    setCurrentStep(2);
                                    setShowCreateModal(true);
                                  }}
                                  title={isAtComparisonLimit 
                                    ? `Limit reached (${comparisonLimit.current}/${comparisonLimit.max}). Click to see upgrade options.` 
                                    : `Add Contractor (${existingCount}/${maxAllowed} contractors)`}
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>
                              );
                            })()}
                            <button 
                              className={`${styles.actionButton} ${styles.deleteAction}`}
                              onClick={() => handleDeleteProject(comparison)}
                              title="Delete Project"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.paginationButton}
                      >
                        <i className="fas fa-chevron-left"></i>
                        Previous
                      </button>
                      
                      <div className={styles.paginationInfo}>
                        <span className={styles.pageNumbers}>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`${styles.pageNumber} ${page === currentPage ? styles.active : ''}`}
                            >
                              {page}
                            </button>
                          ))}
                        </span>
                        <span className={styles.pageInfo}>
                          Page {currentPage} of {totalPages} • {comparisons.length} total projects
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.paginationButton}
                      >
                        Next
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {renderCreateModal()}
      
      {/* AI Processing Overlay */}
      {processingAI && (
        <div className={styles.processingOverlay}>
          <div className={styles.processingModal}>
            <div className={styles.processingHeader}>
              <div className={styles.processingIcon}>
                <i className="fas fa-robot"></i>
              </div>
              <h2>Creating AI Comparison</h2>
              <p>Our AI is analyzing your contractor proposals...</p>
            </div>
            
            <div className={styles.processingContent}>
              <div className={styles.progressSection}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
                <div className={styles.progressText}>
                  {Math.round(processingProgress)}% Complete
                </div>
              </div>
              
              <div className={styles.processingSteps}>
                <div className={`${styles.step} ${processingProgress > 5 ? styles.completed : styles.active}`}>
                  <div className={styles.stepIcon}>
                    {processingProgress > 5 ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <i className="fas fa-upload"></i>
                    )}
                  </div>
                  <span>Uploading documents</span>
                </div>
                
                <div className={`${styles.step} ${processingProgress > 30 ? styles.completed : processingProgress > 5 ? styles.active : ''}`}>
                  <div className={styles.stepIcon}>
                    {processingProgress > 30 ? (
                      <i className="fas fa-check"></i>
                    ) : processingProgress > 5 ? (
                      <div className={styles.spinner}></div>
                    ) : (
                      <i className="fas fa-brain"></i>
                    )}
                  </div>
                  <span>Analyzing proposals with AI</span>
                </div>
                
                <div className={`${styles.step} ${processingProgress > 70 ? styles.completed : processingProgress > 30 ? styles.active : ''}`}>
                  <div className={styles.stepIcon}>
                    {processingProgress > 70 ? (
                      <i className="fas fa-check"></i>
                    ) : processingProgress > 30 ? (
                      <div className={styles.spinner}></div>
                    ) : (
                      <i className="fas fa-chart-line"></i>
                    )}
                  </div>
                  <span>Generating comparison insights</span>
                </div>
                
                <div className={`${styles.step} ${processingProgress >= 100 ? styles.completed : processingProgress > 70 ? styles.active : ''}`}>
                  <div className={styles.stepIcon}>
                    {processingProgress >= 100 ? (
                      <i className="fas fa-check"></i>
                    ) : processingProgress > 70 ? (
                      <div className={styles.spinner}></div>
                    ) : (
                      <i className="fas fa-clipboard-check"></i>
                    )}
                  </div>
                  <span>Finalizing results</span>
                </div>
              </div>
              
              <div className={styles.processingWarning}>
                <div className={styles.warningIcon}>
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className={styles.warningText}>
                  <strong>Please don't close this page</strong>
                  <p>The AI analysis is in progress and may take a few minutes to complete.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModal}>
            <div className={styles.modalHeader}>
              <h2>Delete Project</h2>
              <button className={styles.closeButton} onClick={cancelDeleteProject}>×</button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.deleteWarning}>
                <div className={styles.warningIcon}>
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className={styles.warningContent}>
                  <h3>Are you sure you want to delete this project?</h3>
                  <p>
                    <strong>"{deletingComparison?.name}"</strong> will be permanently deleted along with:
                  </p>
                  <ul className={styles.deleteList}>
                    <li><i className="fas fa-file-alt"></i> All uploaded documents</li>
                    <li><i className="fas fa-chart-bar"></i> Analysis data and comparisons</li>
                    <li><i className="fas fa-users"></i> Contractor information and ratings</li>
                    <li><i className="fas fa-folder"></i> Project folder and files</li>
                  </ul>
                  <div className={styles.warningNote}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>This action cannot be undone!</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={cancelDeleteProject}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button 
                className={styles.confirmDeleteButton} 
                onClick={confirmDeleteProject}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i> Delete Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeOwnerDashboard; 